import { useEffect, type RefObject } from "react";
import { neighbourPane, nextTab, prevTab } from "../model/navigation";
import { activateTab, closePane, focusPane } from "../model/ops";
import { findPane } from "../model/tree";
import type { LayoutTree, NavDirection } from "../model/types";

const EDITABLE =
  'input, textarea, select, [contenteditable=""], [contenteditable="true"], .cm-editor';

const ARROWS: Record<string, NavDirection> = {
  ArrowLeft: "left",
  ArrowRight: "right",
  ArrowUp: "up",
  ArrowDown: "down",
};

export type LayoutKeyEvent = Pick<
  KeyboardEvent,
  "key" | "code" | "metaKey" | "ctrlKey" | "shiftKey" | "altKey" | "target"
>;

function insideEditable(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(EDITABLE) !== null;
}

/**
 * Keyboard map (`mod` is Cmd or Ctrl):
 * - `mod+shift+arrow`: focus the neighbouring pane in that direction
 * - `mod+shift+]` / `mod+shift+[`: next / previous tab in the focused group
 * - `mod+w`: close the focused pane
 *
 * Returns the next tree, or undefined when the event is not a layout shortcut. Arrow shortcuts
 * are left to editable elements (inputs, textareas, contenteditable, CodeMirror), which use them
 * for selection.
 */
export function handleLayoutKey(tree: LayoutTree, event: LayoutKeyEvent): LayoutTree | undefined {
  const mod = event.metaKey || event.ctrlKey;
  if (!mod || event.altKey) return undefined;
  const focused = tree.focusedPaneId;
  if (focused === undefined) return undefined;

  if (event.shiftKey) {
    const direction = ARROWS[event.key];
    if (direction) {
      if (insideEditable(event.target)) return undefined;
      const target = neighbourPane(tree, focused, direction);
      return target === undefined ? undefined : focusPane(tree, target);
    }
    const next = event.code === "BracketRight" || event.key === "]" || event.key === "}";
    const prev = event.code === "BracketLeft" || event.key === "[" || event.key === "{";
    if (next || prev) {
      const group = findPane(tree, focused)?.group;
      if (!group) return undefined;
      const target = next ? nextTab(tree, group.id) : prevTab(tree, group.id);
      return target === undefined ? undefined : activateTab(tree, target);
    }
    return undefined;
  }

  if (event.key === "w" || event.key === "W") return closePane(tree, focused);
  return undefined;
}

/**
 * Attaches the layout shortcuts to the element in `ref`. Only events whose target is inside that
 * element reach the listener. State changes go through `onChange`.
 */
export function useLayoutKeyboard(
  ref: RefObject<HTMLElement | null>,
  tree: LayoutTree,
  onChange: (tree: LayoutTree) => void,
  enabled = true,
): void {
  useEffect(() => {
    const element = ref.current;
    if (!element || !enabled) return;
    const listener = (event: KeyboardEvent): void => {
      const next = handleLayoutKey(tree, event);
      if (next === undefined || next === tree) return;
      event.preventDefault();
      onChange(next);
    };
    element.addEventListener("keydown", listener);
    return () => element.removeEventListener("keydown", listener);
  }, [ref, tree, onChange, enabled]);
}
